import { useState, useEffect } from "react";
import { API } from "../../../api";
import "../../../styles/admin/GlobalPropertiesEditor.css";
import { b } from "framer-motion/client";

export default function GlobalPropertiesEditor() {
	const [properties, setProperties] = useState({
		is_home_restricted: { value: false, type: "boolean", label: "Accueil restreint", color: "from-red-500 to-pink-500" },
		activity_start_date: { value: "", type: "string", label: "Date de début d'activité", color: "from-blue-500 to-cyan-500" },
		estimated_bouquet_count: { value: 50000, type: "number", label: "Nombre de bouquets estimés", color: "from-purple-500 to-pink-500" },
		google_rating_mode: { value: "manual", type: "string", label: "Mode d'évaluation", color: "from-yellow-500 to-orange-500", options: ["manual", "auto"] },
		manual_google_rating: { value: 4.8, type: "number", label: "Évaluation", color: "from-yellow-500 to-orange-500" },
	});

	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(null);
	const [status, setStatus] = useState(null);

	useEffect(() => {
		fetchProperties();
	}, []);

	const fetchProperties = async () => {
		try {
			setLoading(true);
			const data = await API.get("globalproperties");
			
			const updatedProperties = { ...properties };
			if (Array.isArray(data)) {
				data.forEach(prop => {
					if (updatedProperties[prop.key]) {
                        let value = prop.value;
						if (updatedProperties[prop.key].type === "boolean") value = prop.value === true || prop.value === "true";
						updatedProperties[prop.key].value = value;
					}
				});
			}
			setProperties(updatedProperties);
			setStatus(null);
		} catch (error) {
			console.error("Erreur:", error);
			setStatus({ type: "error", message: "Impossible de charger les propriétés" });
		} finally {
			setLoading(false);
		}
	};

	const handleChange = (key, value) => {
		setProperties(prev => ({
			...prev,
			[key]: { ...prev[key], value }
		}));
	};

	const handleSave = async (key) => {
		const prop = properties[key];
		
		try {
			setSaving(key);
			setStatus(null);

			await API.put("globalproperties", {
				p_key: key,
				p_value: prop.value.toString(),
				p_type: prop.type
			});

			setStatus({ type: "success", message: `Succès - ${prop.label} mis à jour` });
			setTimeout(() => setStatus(null), 3000);
		} catch (error) {
			console.error("Erreur:", error);
			setStatus({ type: "error", message: `Erreur - Impossible de mettre à jour` });
		} finally {
			setSaving(null);
		}
	};

	if (loading) {
		return (
			<div className="gp-editor-container">
				<div className="gp-loader">
					<div className="gp-spinner"></div>
					<p>Chargement de la configuration...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="gp-editor-container">
			<div className="gp-header-section">
				<h1 className="gp-title">Configuration Générale de l'Accueil</h1>
				<p className="gp-subtitle">Gérez les paramètres de la page d'accueil</p>
			</div>

			{status && (
				<div className={`gp-status gp-status-${status.type}`}>
					<div className="gp-status-content">
						<span className="gp-status-icon">
							{status.type === "success" ? "✓" : status.type === "error" ? "✕" : "ℹ"}
						</span>
						<span>{status.message}</span>
					</div>
				</div>
			)}

			<div className="gp-content">
				{/* Section Google Rating - Regroupée */}
				<div className="gp-section">
					<h2 className="gp-section-title">Évaluation Google</h2>
					<div className="gp-google-widget">
						<div className="gp-google-preview">
							<div className="gp-google-stars">
								{Array.from({ length: 5 }).map((_, i) => (
									<span key={i} className={`gp-star ${i < Math.floor(properties.manual_google_rating.value) ? "gp-star-filled" : ""}`}>★</span>
								))}
								<span className="gp-google-rating-value">{properties.manual_google_rating.value}</span>
							</div>
							<p className="gp-google-label">Aperçu de l'évaluation</p>
						</div>

						<div className="gp-google-controls">
							<div className="gp-control-item">
								<label htmlFor="google_rating_mode" className="gp-control-label">Mode</label>
								<select
									id="google_rating_mode"
									value={properties.google_rating_mode.value}
									onChange={(e) => handleChange("google_rating_mode", e.target.value)}
									disabled={saving === "google_rating_mode"}
									className="gp-select gp-select-mode"
								>
									<option value="manual">Manuel</option>
									<option value="auto">Automatique</option>
								</select>
								<button
									onClick={() => handleSave("google_rating_mode")}
									disabled={saving === "google_rating_mode"}
									className={`gp-btn-save gp-btn-inline ${saving === "google_rating_mode" ? "gp-loading" : ""}`}
								>
									{saving === "google_rating_mode" ? "" : "Sauvegarder"}
								</button>
							</div>

							<div className="gp-control-item">
								<label htmlFor="manual_google_rating" className="gp-control-label">Note</label>
								<input
									type="number"
									id="manual_google_rating"
									value={properties.manual_google_rating.value}
									onChange={(e) => handleChange("manual_google_rating", parseFloat(e.target.value) || "")}
									disabled={saving === "manual_google_rating"}
									className="gp-input gp-input-rating"
									step="0.1"
									min="0"
									max="5"
								/>
								<button
									onClick={() => handleSave("manual_google_rating")}
									disabled={saving === "manual_google_rating"}
									className={`gp-btn-save gp-btn-inline ${saving === "manual_google_rating" ? "gp-loading" : ""}`}
								>
									{saving === "manual_google_rating" ? "" : "Sauvegarder"}
								</button>
							</div>
						</div>
					</div>
				</div>

				{/* Section Autres paramètres */}
				<div className="gp-section">
					<h2 className="gp-section-title">Autres Paramètres</h2>
					<div className="gp-properties-grid">
						{Object.entries(properties).filter(([key]) => !key.includes("google")).map(([key, prop]) => (
							<div key={key} className="gp-property-card">
								<div className="gp-card-inner">
									<div className="gp-card-content">
										<h3 className="gp-card-title">{prop.label}</h3>
										<div className="gp-input-wrapper">
											{prop.type === "boolean" ? (
												<label className="gp-toggle">
													<input type="checkbox" checked={prop.value} onChange={(e) => handleChange(key, e.target.checked)} disabled={saving === key} />
													<span className="gp-toggle-slider"></span>
													<span className="gp-toggle-label">{prop.value ? "Activé" : "Désactivé"}</span>
												</label>
											) : prop.type === "string" && prop.options ? (
												<select
													value={prop.value}
													onChange={(e) => handleChange(key, e.target.value)}
													disabled={saving === key}
													className="gp-input gp-select-compact"
												>
													{prop.options.map(option => (
														<option key={option} value={option}>
															{option === "manual" ? "Manuel" : "Automatique"}
														</option>
													))}
												</select>
											) : prop.type === "string" && key === "activity_start_date" ? (
												<input
													type="date"
													value={prop.value}
													onChange={(e) => handleChange(key, e.target.value)}
													disabled={saving === key}
													className="gp-input gp-input-compact"
												/>
											) : prop.type === "number" ? (
												<input
													type="number"
													value={prop.value}
													onChange={(e) => handleChange(key, parseFloat(e.target.value) || "")}
													disabled={saving === key}
													className="gp-input gp-input-compact"
													step="1"
												/>
											) : (
												<input
													type="text"
													value={prop.value}
													onChange={(e) => handleChange(key, e.target.value)}
													disabled={saving === key}
													className="gp-input gp-input-compact"
												/>
											)}
										</div>
									</div>
									<button
										onClick={() => handleSave(key)}
										disabled={saving === key}
										className={`gp-btn-save gp-btn-inline ${saving === key ? "gp-loading" : ""}`}
									>
										{saving === key ? "" : "Sauvegarder"}
									</button>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}